/**
 * Liga os dados de focos ao loop: recarrega quando a janela (`range`) muda
 * (com espera de 400 ms e cancelamento da carga anterior), quando uma fonte é
 * ligada e quando se pede atualização (`refreshFires`).
 *
 * O app-core desliza `range` quando chegam imagens novas e só chama
 * `refreshFires` (gancho da atualização automática) quando a janela NÃO
 * mudou. Mesmo assim, as duas vias não disputam:
 * - uma só espera (timer) para as duas — a última chamada vale;
 * - uma carga já em andamento para a MESMA janela não é reiniciada (o pedido
 *   novo espera a mesma);
 * - se a janela muda no meio, a carga anterior é cancelada, mas o download de
 *   um dia do diário que continua necessário é aproveitado pela carga nova
 *   (api.ts: downloads por dia compartilhados); dias que saíram da janela são
 *   cancelados.
 */
import { waitForFrameRendered } from '../loop/render';
import { useTimeStore, type TimeRange } from '../state/time';
import { formatBytes } from '../lib/format';
import { inAmazoniaBbox, type PolygonIndex, type TenMinAmazoniaTest } from './amazonia';
import { loadAmazoniaPolygon, loadMunicipioCodes } from './amazoniaData';
import { abortDailyJobs, buildTenMinTable, estimateDailyDownload, load10min, loadDaily, remarkAmazonia } from './api';
import { describeError } from './errors';
import { abortable, isAbortError } from './net';
import { idleStatus, useFireStore } from './store';
import type { FireSource } from './types';

const RANGE_DEBOUNCE_MS = 400;
/** Espera máxima pela 1ª imagem do loop antes de liberar o diário. */
const DAILY_GATE_MAX_MS = 20_000;
/** Espera máxima por um momento ocioso do navegador (depois da 1ª imagem). */
const DAILY_IDLE_MS = 2_000;

let refCount = 0;
let unsubscribers: (() => void)[] = [];
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const controllers: Partial<Record<FireSource, AbortController>> = {};
/** Carga em andamento por fonte (janela + promessa), para não reiniciar a mesma. */
const inflight: Partial<Record<FireSource, { key: string; promise: Promise<void> }>> = {};
let polygon: PolygonIndex | null = null;
let dailyGate: Promise<void> | null = null;
let dailyGateOpen = false;

/** Resolve quando o loop já tem quadros (a janela e as imagens do GOES existem). */
function untilFramesKnown(): Promise<void> {
  if (useTimeStore.getState().frames.length > 0) return Promise.resolve();
  return new Promise((resolve) => {
    const unsub = useTimeStore.subscribe((s) => {
      if (s.frames.length === 0) return;
      unsub();
      resolve();
    });
  });
}

function idle(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    if (ric) ric(() => resolve(), { timeout: timeoutMs });
    else setTimeout(resolve, Math.min(300, timeoutMs));
  });
}

/**
 * O diário (~3,6 MB por dia, sem gzip) só começa depois que a 1ª imagem do
 * loop estiver na tela e o navegador ficar ocioso — para não disputar banda
 * com a abertura do portal. Vale só para a 1ª carga da sessão.
 */
function waitForDailyGate(): Promise<void> {
  dailyGate ??= (async () => {
    await untilFramesKnown();
    await waitForFrameRendered(DAILY_GATE_MAX_MS).catch(() => {});
    await idle(DAILY_IDLE_MS);
  })();
  return dailyGate;
}

const rangeKey = (r: TimeRange) => `${r.start}|${r.end}`;

/** Teste de Amazônia Legal dos focos de 10 min (polígono, ou caixa enquanto não há polígono). */
const tenMinAmazonia: TenMinAmazoniaTest = (lon, lat, x, y) =>
  polygon ? polygon.contains(x, y) : inAmazoniaBbox(lon, lat);

/** Inicia a sincronização (idempotente, com contagem de referências). Devolve a parada. */
export function startFireSync(): () => void {
  refCount++;
  if (refCount === 1) start();
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    refCount--;
    if (refCount === 0) stop();
  };
}

function start() {
  const { setStatus, sources } = useFireStore.getState();
  (Object.keys(sources) as FireSource[]).forEach((s) => setStatus(s, sources[s] ? idleStatus() : idleStatus('off')));

  unsubscribers.push(
    useTimeStore.subscribe((s, prev) => {
      const a = s.range;
      const b = prev.range;
      if (a?.start === b?.start && a?.end === b?.end) return;
      scheduleLoad();
    }),
    useFireStore.subscribe((s, prev) => {
      if (s.sources === prev.sources) return;
      (Object.keys(s.sources) as FireSource[]).forEach((src) => {
        if (s.sources[src] === prev.sources[src]) return;
        if (s.sources[src]) void loadSource(src);
        else disableSource(src);
      });
    }),
  );

  // Polígono da Amazônia Legal para o 10 min (quando disponível).
  void loadAmazoniaPolygon().then((index) => {
    if (!index || refCount === 0) return;
    polygon = index;
    const st = useFireStore.getState();
    st.setAmazoniaFilter({ goes10min: 'poligono' });
    if (st.goes10min) st.setData({ goes10min: remarkAmazonia(st.goes10min, tenMinAmazonia) });
  });

  scheduleLoad(0);
}

function stop() {
  unsubscribers.forEach((u) => u());
  unsubscribers = [];
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  (Object.keys(controllers) as FireSource[]).forEach((s) => {
    controllers[s]?.abort();
    delete controllers[s];
    delete inflight[s];
  });
  abortDailyJobs();
}

function scheduleLoad(delay = RANGE_DEBOUNCE_MS) {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    const { sources } = useFireStore.getState();
    (Object.keys(sources) as FireSource[]).forEach((s) => {
      if (sources[s]) void loadSource(s);
    });
  }, delay);
}

/**
 * Força nova busca das fontes ligadas (revalida os arquivos que podem ter
 * mudado). Se já há carga em andamento para a mesma janela, não a reinicia.
 */
export function refreshFires(): void {
  if (refCount === 0) return;
  scheduleLoad(0);
}

function disableSource(source: FireSource) {
  controllers[source]?.abort();
  delete controllers[source];
  delete inflight[source];
  const st = useFireStore.getState();
  st.setStatus(source, idleStatus('off'));
  st.setData(source === 'goes10min' ? { goes10min: null } : { diario: [] });
}

/** Carrega a fonte para a janela atual (reaproveita a carga em andamento da mesma janela). */
export function loadSource(source: FireSource): Promise<void> {
  const range = useTimeStore.getState().range;
  if (!range || !useFireStore.getState().sources[source]) return Promise.resolve();
  const key = rangeKey(range);
  const current = inflight[source];
  if (current && current.key === key) return current.promise;

  controllers[source]?.abort();
  const ac = new AbortController();
  controllers[source] = ac;
  const promise = runLoad(source, range, ac).finally(() => {
    if (inflight[source]?.promise === promise) delete inflight[source];
    if (controllers[source] === ac) delete controllers[source];
  });
  inflight[source] = { key, promise };
  return promise;
}

async function runLoad(source: FireSource, range: TimeRange, ac: AbortController): Promise<void> {
  const t0 = performance.now();
  useFireStore.getState().setStatus(source, { state: 'loading', progress: 0, error: null, errorDetail: null, message: 'Buscando no INPE…' });
  try {
    if (source === 'goes10min') {
      const res = await load10min(range, {
        signal: ac.signal,
        onProgress: (p) =>
          useFireStore.getState().setStatus(source, {
            progress: p.total ? p.done / p.total : 1,
            message: `${p.done} de ${p.total} arquivos`,
          }),
      });
      if (ac.signal.aborted) return;
      const table = buildTenMinTable(res.fires, tenMinAmazonia);
      const s = useFireStore.getState();
      s.setData({ goes10min: table });
      s.setStatus(source, {
        state: 'ready',
        progress: 1,
        message: `${res.files} arquivos (${res.downloaded} novos)`,
        warning: res.warning,
        note: res.note,
        latest: table.length ? table.t[table.length - 1]! : null,
        publicadoAte: res.publicadoAte,
        updatedAt: Date.now(),
        bytes: res.bytes,
      });
    } else {
      if (!dailyGateOpen) {
        useFireStore.getState().setStatus(source, { message: 'Começa depois que as imagens do satélite aparecerem…' });
        await abortable(waitForDailyGate(), ac.signal);
        dailyGateOpen = true;
      }
      const codes = await loadMunicipioCodes();
      if (ac.signal.aborted) return;
      useFireStore.getState().setAmazoniaFilter({ diario: codes ? 'municipios' : 'aproximado' });
      const est = estimateDailyDownload(range);
      const big = est.files > 2 ? ` (~${formatBytes(est.bytes)})` : '';
      useFireStore.getState().setStatus(source, { message: `Baixando ${est.files} arquivo(s) diário(s)${big}…` });
      const res = await loadDaily(range, {
        signal: ac.signal,
        codes: codes ? [...codes] : null,
        onProgress: (p) =>
          useFireStore.getState().setStatus(source, {
            progress: p.total ? p.done / p.total : 1,
          }),
      });
      if (ac.signal.aborted) return;
      const s = useFireStore.getState();
      s.setData({ diario: res.tables });
      let latest: number | null = null;
      for (const tb of res.tables) if (tb.length) latest = Math.max(latest ?? 0, tb.t[tb.length - 1]!);
      s.setStatus(source, {
        state: 'ready',
        progress: 1,
        message: `${res.days.length} dia(s)${res.downloaded ? `, ${formatBytes(res.bytes)} baixados` : ''}`,
        warning: res.warning,
        note: res.note,
        latest,
        updatedAt: Date.now(),
        bytes: res.bytes,
      });
    }
    if (import.meta.env.DEV) console.debug(`[focos] ${source} carregado em ${Math.round(performance.now() - t0)} ms`);
  } catch (e) {
    if (isAbortError(e) || ac.signal.aborted) return;
    console.warn(`[focos] falha ao carregar ${source}`, e);
    const { message, detail } = describeError(e);
    useFireStore.getState().setStatus(source, {
      state: 'error',
      progress: 0,
      message: null,
      error: message,
      errorDetail: detail,
    });
  }
}
